// src/oura/newscan/fixtures/samples.ts
//
// The sample businesses the fixture engine drafts for, keyed by domain. All of
// it is invented. Each sample carries what the website read finds, the words
// the draft would pick (with where each came from and the other picks), the
// buildings, stations and landmarks near each outlet, the searches a
// connected Search Console would report, and, for the two existing clients,
// the approved set they are on today.
//
//   samplephysio.my          new, Visit us. The clinic scenario.
//   samplesleep.my           new, Online (brand mode, shoppers only, Malaysia). The store scenario.
//                            Picked as Both (or Visit us with a showroom) it raises "The local
//                            questions are written for clinics, studios and gyms" with Use
//                            Online instead: the local questions do not fit a mattress shop.
//   sampleglow.my            new, Both: a skin clinic in Damansara Uptown (PJ) that also sells
//                            its skincare online. Drafts as Local v4.5, the clinic is the outlet.
//   samplesupps.com          new, Online (brand mode, sells to businesses, US / Singapore / Malaysia).
//   thepilatescollective.my  existing client on v4.1 wording for 9 rows: edit and recipe update.
//   riversidedental.co       existing client: edit.
//
// Joined journey (journeySamples.ts lists them for both sample stores):
//   samplesmile.my           signed dental clinic, pitched on the platform: its first_check draft
//                            copies the pitched 50, then the form adds Invisalign and names
//                            Petaling Jaya (a town), and the quote sells 3 outlets against 2.
//   samplekids.my            signed, not paid yet, pitched on the platform (one outlet).
//   samplelush.my            signed, pitched on the Mac: the site read takes the clinic's name and the city
//                            as outlets and names four non-places; the form and the quote say 2 branches.
//   samplespine.my           signed, pitched on the Mac: the first check drafts from the site and the form.
//   samplesight.my           signed 24-outlet chain, pitched on the Mac: 9 outlets get questions.
//
// Any other domain reads as low confidence with empty fields (manual entry).

import type { BuyMode, ScanDraftBusiness, ScanDraftDetected, ScanDraftLastResult, WordOrigin } from "@service/aeo/scanDraftTypes";
import { OLD_LOCAL_RECIPE, RECIPES, type RecipeInfo } from "./recipe";
import { SLEEP_BRAND, SUPPS_BRAND, type BrandSet } from "./brandSamples";
import { SIGHT_OUTLETS } from "./journeySamples";

export interface FixtureWord {
    value: string;
    origin: WordOrigin;
    alternatives: string[];
    /** The read found nothing for it: the draft leaves it blank. */
    missing?: boolean;
    /** Where the alternatives come from, when they have a source ("Seen on 3 nearby physio sites: RM100 to RM150"). */
    source?: string;
}

export interface PlaceFacts {
    building?: string;
    transit?: string;
    landmark?: string;
}

export interface FixtureSearch {
    query: string;
    searches: number;
    clicks: number;
    position: number | null;
    /** The variable values a question must be filled with to match this search. */
    match: Record<string, string>;
    /** Brand rows (no words): the words a question's text must hold to match this search. */
    matchText?: string;
    /** Unmatched: the word edit that would make a question ask it. */
    candidate?: { variable: string; value: string };
}

export interface FixtureExisting {
    businessId: string;
    business: ScanDraftBusiness;
    recipe: RecipeInfo;
    assessmentId: string;
    /** Row key -> the v4.1 wording the approved set still asks. */
    oldTemplates: Record<string, string>;
    /** Row key -> that wording's result per engine in the latest scan. A key left out was not measured. */
    lastResults?: Record<string, ScanDraftLastResult[]>;
}

export interface FixtureSample {
    domain: string;
    detected: ScanDraftDetected;
    /** Distinctive words of the business name: a question naming one fails the lint. */
    nameTokens: string[];
    languageShare: { ms: number; zh: number };
    words: Record<string, FixtureWord>;
    wordsByBuy?: Partial<Record<BuyMode, Record<string, FixtureWord>>>;
    /** Lower-cased area -> what is near that outlet. */
    places: Record<string, PlaceFacts>;
    searches: FixtureSearch[];
    /** Row key -> a rewrite template that clears a duplicate. */
    rewrites: Record<string, string>;
    existing?: FixtureExisting;
    /** Brand mode (Online): the pre-authored 50, their Google suggestions and the brand's banned words. */
    brand?: BrandSet;
    /**
     * False: the local questions (a treatment or a class near an outlet) do not
     * fit this business, so a local buy answer raises "Use Online instead".
     * Absent: they fit.
     */
    localFits?: boolean;
    /**
     * Joined journey: the business the platform pitch was drafted from, and the
     * recipe it was written on. The pitch's date, BD and scan id are in
     * journeySamples.ts. A new_scan draft started on the site this session
     * stands in for it. Absent: no platform pitch.
     */
    pitch?: { business: ScanDraftBusiness; stamp: string };
    /** Names the site uses that the read did not take as outlets ("The site names X, which is not in your outlets"). */
    siteNames?: string[];
}

const READ_PAGES = ["Home page", "Main pages", "Contact page", "About page"];

const research = (value: string, ...alternatives: string[]): FixtureWord => ({ value, origin: "research", alternatives });
const site = (value: string, ...alternatives: string[]): FixtureWord => ({ value, origin: "site", alternatives });
const services = (value: string, ...alternatives: string[]): FixtureWord => ({ value, origin: "services", alternatives });
const notFound = (...alternatives: string[]): FixtureWord => ({ value: "", origin: "fallback", alternatives, missing: true });

/* ------------------------------------------------------------------------- *
 * samplephysio.my
 * ------------------------------------------------------------------------- */

const PHYSIO: FixtureSample = {
    domain: "samplephysio.my",
    detected: {
        businessName: "Sample Physio",
        category: "Physiotherapy",
        city: "KL",
        outlets: [{ name: "Bangsar", town: "KL" }],
        services: ["Back pain treatment", "Dry needling", "Sports injury rehab"],
        buyGuess: "visit",
        buyWhy: "an outlets page with one address, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: ["Home page", "Treatments page", "Outlets page", "About page"],
    },
    nameTokens: ["sample"],
    languageShare: { ms: 0.2, zh: 0.1 },
    words: {
        pain_point: research("back pain", "lower back pain", "sciatica"),
        pain_point_2: research("neck and shoulder stiffness", "stiff neck", "shoulder pain"),
        condition: site("slipped disc", "frozen shoulder", "scoliosis"),
        common_concern: research("pain after treatment", "a first visit after an injury", "a recurring injury"),
        core_service: services("lower back pain treatment", "back pain physio", "spine rehab"),
        secondary_service_1: services("dry needling", "cupping", "acupuncture"),
        secondary_service_2: services("sports injury rehab", "ACL rehab", "ankle sprain rehab"),
        secondary_service_3: site("scoliosis treatment", "posture correction"),
        secondary_service_4: site("post-surgery rehab", "knee replacement rehab", "pilates rehab"),
        secondary_service_5: site("shoulder rehab", "frozen shoulder treatment"),
        secondary_service_6: research("knee pain treatment", "runner's knee treatment"),
        differentiator: site("shockwave therapy", "spinal decompression"),
        audience_1: research("office workers", "desk workers"),
        audience_2: research("runners", "golfers"),
        audience_3: site("seniors", "pregnant women"),
        priced_service: site("a physio session", "a back pain assessment"),
        price_point: notFound("RM120", "RM150"),
        promo_phrase: site("first session promo", "new patient discount"),
        place_noun: research("physio clinic", "physiotherapy centre"),
        category: site("physiotherapist"),
        alternative_category: research("chiropractor", "an orthopaedic doctor"),
        situational: research("on Sunday", "after work"),
        situational_2: research("after 8pm", "on public holidays"),
        pain_point_ms: research("sakit belakang"),
        priced_service_ms: research("sesi fisioterapi"),
        secondary_service_1_zh: research("干针"),
        condition_zh: research("椎间盘突出"),
        core_service_zh: research("腰痛治疗"),
        place_noun_zh: research("物理治疗中心"),
    },
    wordsByBuy: {
        we_go_to_them: { place_noun: site("home physio", "home physiotherapy") },
    },
    places: {
        bangsar: { building: "Bangsar Village II", transit: "Bangsar LRT", landmark: "Telawi Street" },
        "subang jaya": { building: "Subang Parade", transit: "SS15 LRT", landmark: "Taylor's Lakeside" },
        "mont kiara": { building: "Publika", transit: "Mont Kiara bus hub", landmark: "Arcoris" },
        "damansara heights": { building: "Pavilion Damansara Heights", transit: "Semantan MRT", landmark: "Jalan Setiabakti" },
        // The sub-areas the Business step offers as examples (city_rule anchor_is_not_a_town).
        ss15: { building: "SS15 Courtyard", transit: "SS15 LRT", landmark: "Subang Parade" },
        "usj taipan": { building: "Taipan Business Centre", transit: "USJ 21 LRT", landmark: "Main Place Mall" },
        "section 17": { building: "Atria Shopping Gallery", transit: "Taman Jaya LRT", landmark: "Section 17 night market" },
        "damansara uptown": { building: "Uptown 5", transit: "Taman Bahagia LRT", landmark: "SS2 Night Market" },
    },
    searches: [
        { query: "physio bangsar", searches: 2400, clicks: 140, position: 6.2, match: { place_noun: "physio clinic", area: "Bangsar" } },
        { query: "back pain specialist kl", searches: 1600, clicks: 38, position: 11.4, match: { pain_point: "back pain", city: "KL" } },
        { query: "slipped disc treatment kl", searches: 1100, clicks: 22, position: 14.8, match: { condition: "slipped disc", city: "KL" } },
        { query: "back pain bangsar", searches: 880, clicks: 61, position: 4.9, match: { pain_point: "back pain", area: "Bangsar" } },
        { query: "dry needling bangsar", searches: 590, clicks: 47, position: 3.1, match: { secondary_service_1: "dry needling", area: "Bangsar" } },
        { query: "physio bangsar price", searches: 320, clicks: 12, position: 8.5, match: { priced_service: "a physio session", area: "Bangsar" } },
        {
            query: "frozen shoulder treatment kl",
            searches: 590,
            clicks: 9,
            position: 18.2,
            match: { secondary_service_3: "frozen shoulder treatment" },
            candidate: { variable: "secondary_service_3", value: "frozen shoulder treatment" },
        },
    ],
    rewrites: { C42: "Which {place_noun} in {city} do locals rate best?" },
};

/* ------------------------------------------------------------------------- *
 * samplesleep.my
 * ------------------------------------------------------------------------- */

const SLEEP: FixtureSample = {
    domain: "samplesleep.my",
    // v4.5: the read lists no showroom (with one the read rule would make this Both: a door is
    // local). The Both path: staff pick Both and add the Jaya One showroom in PJ by hand.
    detected: {
        businessName: "Sample Sleep Co.",
        category: "Mattress store",
        city: "PJ",
        outlets: [],
        services: ["Mattresses", "Pillows", "Bed frames"],
        buyGuess: "online",
        buyWhy: "a cart and delivery to all of Malaysia, and no address customers visit",
        market: "Malaysia",
        signals: ["a cart", "delivery to all of Malaysia", "no address customers visit"],
        marketsGuess: [{ code: "my", label: "Malaysia" }],
        sellsToBusinessesGuess: false,
        partnerProductsGuess: [],
        confidence: "high",
        reason: null,
        pages: ["Home page", "Shop page", "Delivery page", "Contact page"],
    },
    nameTokens: ["sample"],
    languageShare: { ms: 0.1, zh: 0.2 },
    words: {
        product_noun: site("mattress", "bed"),
        core_product: services("pocket spring mattress", "hybrid mattress", "latex mattress"),
        product_family: site("bedding", "bedroom furniture"),
        secondary_service_1: services("latex pillows", "memory foam pillows"),
        secondary_service_2: site("mattress toppers", "bed toppers"),
        secondary_service_3: research("cooling mattresses", "mattresses for hot weather"),
        secondary_service_4: site("kids mattresses", "single mattresses"),
        secondary_service_5: site("waterproof mattress protectors"),
        secondary_service_6: services("storage bed frames", "divan beds"),
        differentiator: site("organic latex mattresses", "locally made mattresses"),
        differentiator_2: site("zoned support", "edge support"),
        warranty: site("15-year warranty", "10-year warranty"),
        delivery_promise: site("free delivery and old mattress disposal", "free delivery"),
        audience_1: research("side sleepers", "back sleepers"),
        audience_2: research("couples"),
        audience_3: research("students", "renters"),
        audience_4: research("seniors"),
        audience_5: research("a first home"),
        common_concern: research("a mattress that sags", "back support", "less motion transfer"),
        pain_point: research("back pain", "hip pain"),
        pain_point_2: research("night sweats", "sleeping hot"),
        condition: research("a couple who like different firmness"),
        situation: research("a small condo bedroom"),
        store_noun: research("online mattress store", "mattress shop"),
        price_point: site("RM1,500", "RM1,000"),
        payment_option: site("Atome", "instalments"),
        quality: research("durability", "comfort"),
        alternative_category: research("department store brands"),
        situational: site("the next day", "within 3 days"),
        setup_phrase: site("comes rolled in a box"),
        trial_offer: site("100-night trial"),
        return_reason: research("it feels too firm"),
        promo_phrase: research("an 11.11 sale", "a year-end sale"),
        remote_region: research("East Malaysia", "Sabah"),
    },
    // Both is Local v4.5: the showroom is an outlet, so it needs the local words. Its products sit
    // in the specialty and intent rows.
    wordsByBuy: {
        both: {
            pain_point: research("a sagging mattress", "a mattress that is too soft"),
            pain_point_2: research("a mattress that is too firm", "sleeping hot"),
            condition: research("snoring", "sleeping hot"),
            common_concern: research("back support", "less motion transfer"),
            core_service: services("mattress fitting", "a mattress trial"),
            place_noun: research("mattress shop", "mattress showroom"),
            secondary_service_1: services("latex pillows", "memory foam pillows"),
            secondary_service_2: site("mattress toppers", "bed toppers"),
            secondary_service_3: research("cooling mattresses", "mattresses for hot weather"),
            secondary_service_4: site("kids mattresses", "single mattresses"),
            secondary_service_5: services("pocket spring mattresses", "hybrid mattresses"),
            secondary_service_6: services("storage bed frames", "divan beds"),
            differentiator: site("organic latex mattresses", "locally made mattresses"),
            audience_1: research("side sleepers", "back sleepers"),
            audience_2: research("couples"),
            audience_3: research("seniors"),
            priced_service: site("a queen mattress", "a king mattress"),
            price_point: site("RM1,500", "RM1,000"),
            promo_phrase: research("year-end sale", "11.11 sale"),
            category: research("a mattress shop"),
            alternative_category: research("a department store", "an online mattress brand"),
            situational: site("on Sunday", "after 9pm"),
            situational_2: site("on public holidays", "late at night"),
        },
    },
    places: {
        "jaya one": { building: "The School", transit: "Taman Paramount LRT", landmark: "Universiti Malaya" },
    },
    searches: [
        // `match` counts a Both draft's templated rows (local words); `matchText` a brand draft's rows (no words).
        { query: "latex pillow malaysia", searches: 2900, clicks: 210, position: 5.4, match: { secondary_service_1: "latex pillows" } },
        {
            query: "pocket spring mattress",
            searches: 2400,
            clicks: 96,
            position: 9.1,
            match: { core_product: "pocket spring mattress" },
            matchText: "pocket spring mattress in malaysia",
        },
        { query: "mattress topper malaysia", searches: 1900, clicks: 54, position: 12.6, match: { secondary_service_2: "mattress toppers" }, matchText: "mattress topper in malaysia" },
        { query: "mattress delivery east malaysia", searches: 1300, clicks: 31, position: 7.7, match: { remote_region: "East Malaysia" } },
        { query: "mattress 11.11 sale", searches: 1000, clicks: 40, position: 6.3, match: { promo_phrase: "an 11.11 sale" }, matchText: "11.11" },
        {
            query: "king size mattress malaysia",
            searches: 3600,
            clicks: 18,
            position: 21.5,
            match: { secondary_service_4: "king size mattresses" },
            matchText: "king size mattress in malaysia",
            candidate: { variable: "secondary_service_4", value: "king size mattresses" },
        },
    ],
    // The opposite-meaning row: ask the worry the way people mean it (the brand row 22 on Online).
    rewrites: { "B:p22": "Which bed won't sag after a few years" },
    brand: SLEEP_BRAND,
    localFits: false,
};

/* ------------------------------------------------------------------------- *
 * samplesupps.com (brand mode, ingredient brand, sells to businesses)
 * ------------------------------------------------------------------------- */

const SUPPS: FixtureSample = {
    domain: "samplesupps.com",
    detected: {
        businessName: "Sample Botanics",
        category: "Curcumin extract",
        city: "",
        outlets: [],
        services: ["Curcumin extract", "Turmeric capsules", "Private label"],
        buyGuess: "online",
        buyWhy: "no address customers visit, a Wholesale page and distributors in the US and Singapore",
        market: "Malaysia",
        signals: ["no address customers visit", "a Wholesale page", "distributors in the US", "distributors in Singapore"],
        marketsGuess: [
            { code: "us", label: "the US" },
            { code: "sg", label: "Singapore" },
            { code: "my", label: "Malaysia" },
        ],
        sellsToBusinessesGuess: true,
        partnerProductsGuess: ["Sample Labs ProBlend", "Sample Nutrition JointPlus"],
        confidence: "high",
        reason: null,
        pages: ["Home page", "Science page", "Wholesale page", "Contact page"],
    },
    nameTokens: ["sample", "botanics", "samplecurc"],
    languageShare: { ms: 0.05, zh: 0.1 },
    words: {
        // Drafted first as an 8-word claim: the selling-point guard (5 words, never a claim) catches it.
        differentiator: site("the most absorbable clinically proven curcumin on earth", "water soluble extract", "standardised 95% extract"),
    },
    places: {},
    searches: [
        { query: "most bioavailable curcumin", searches: 4400, clicks: 35, position: 14.2, match: {}, matchText: "most bioavailable curcumin" },
        { query: "curcumin private label", searches: 720, clicks: 41, position: 4.8, match: {}, matchText: "private label" },
        { query: "curcumin bioavailability", searches: 1600, clicks: 88, position: 6.1, match: {}, matchText: "curcumin bioavailability" },
    ],
    rewrites: {},
    brand: SUPPS_BRAND,
};

/* ------------------------------------------------------------------------- *
 * sampleglow.my (Both: a skin clinic with an online shop)
 * ------------------------------------------------------------------------- */

const GLOW: FixtureSample = {
    domain: "sampleglow.my",
    // A door and a cart: the clinic is found by place, so Both. Its skincare sits in the other questions.
    detected: {
        businessName: "Sample Glow Skin Clinic",
        category: "Aesthetic clinic",
        city: "PJ",
        outlets: [{ name: "Damansara Uptown", town: "PJ" }],
        services: ["Acne scar treatment", "Pigmentation treatment", "Facials", "Skincare shop"],
        buyGuess: "both",
        buyWhy: "a clinic address and a shop cart",
        market: "Malaysia",
        signals: ["a clinic address", "a shop cart", "delivery to all of Malaysia"],
        confidence: "high",
        reason: null,
        pages: ["Home page", "Treatments page", "Shop page", "Contact page"],
    },
    nameTokens: ["sample", "glow"],
    languageShare: { ms: 0.15, zh: 0.2 },
    words: {
        pain_point: research("acne scars", "acne", "large pores"),
        pain_point_2: research("pigmentation", "dark spots", "uneven skin tone"),
        condition: site("melasma", "rosacea", "eczema"),
        common_concern: research("sensitive skin", "skin that breaks out easily", "a first facial"),
        core_service: services("acne scar treatment", "acne treatment", "laser for acne scars"),
        secondary_service_1: services("facials", "deep cleansing facials", "acne facials"),
        secondary_service_2: site("chemical peels", "glycolic peels"),
        secondary_service_3: services("pigmentation treatment", "melasma laser"),
        secondary_service_4: site("pico laser", "laser toning"),
        secondary_service_5: site("skin boosters", "microneedling"),
        secondary_service_6: research("mole removal", "wart removal"),
        differentiator: site("LED light therapy", "a doctor at every session"),
        audience_1: research("teenagers", "teens with acne"),
        audience_2: research("brides-to-be", "men"),
        audience_3: research("men", "mums-to-be"),
        priced_service: site("a hydrafacial", "an acne facial"),
        price_point: site("RM250", "RM200"),
        promo_phrase: site("first-visit promo", "first facial promo"),
        place_noun: research("skin clinic", "aesthetic clinic", "facial clinic"),
        category: research("a dermatologist"),
        alternative_category: research("a beauty salon", "a facial spa"),
        situational: research("on Sunday", "after work"),
        situational_2: research("after 8pm", "on public holidays"),
        pain_point_ms: research("parut jerawat"),
        priced_service_ms: research("rawatan hydrafacial"),
        secondary_service_1_zh: research("面部护理"),
        condition_zh: research("黄褐斑"),
        core_service_zh: research("痘印治疗"),
        place_noun_zh: research("皮肤诊所"),
    },
    places: {
        "damansara uptown": { building: "Uptown 5", transit: "Taman Bahagia LRT", landmark: "SS2 Night Market" },
    },
    searches: [
        { query: "acne scar treatment pj", searches: 1900, clicks: 74, position: 7.3, match: { core_service: "acne scar treatment", city: "PJ" } },
        { query: "hydrafacial damansara uptown", searches: 720, clicks: 52, position: 3.8, match: { priced_service: "a hydrafacial", area: "Damansara Uptown" } },
        { query: "skin clinic damansara uptown", searches: 590, clicks: 41, position: 4.4, match: { place_noun: "skin clinic", area: "Damansara Uptown" } },
        {
            query: "melasma treatment pj",
            searches: 480,
            clicks: 6,
            position: 16.9,
            match: { condition: "melasma", city: "PJ" },
        },
    ],
    rewrites: {},
};

/* ------------------------------------------------------------------------- *
 * thepilatescollective.my (existing client, v4.1 wording on 9 rows)
 * ------------------------------------------------------------------------- */

const PILATES_BUSINESS: ScanDraftBusiness = {
    businessName: "The Pilates Collective",
    category: "Pilates studio",
    buy: "visit",
    market: null,
    city: "KL",
    outlets: [
        { name: "SS15", town: "Subang Jaya", source: "site" },
        { name: "Bangsar", town: "KL", source: "site" },
    ],
    services: [
        { name: "Reformer pilates", source: "site" },
        { name: "Group classes", source: "site" },
        { name: "Personal training", source: "site" },
    ],
    languages: { ms: "auto", zh: "auto" },
    rivals: ["Reform Studio KL"],
    otherNames: [],
    // The backend keeps scope.name_exclusions here and returns it on every draft.
    notUs: [],
    requestedBy: null,
};

const PILATES: FixtureSample = {
    domain: "thepilatescollective.my",
    detected: {
        businessName: "The Pilates Collective",
        category: "Pilates studio",
        city: "KL",
        outlets: [
            { name: "SS15", town: "Subang Jaya" },
            { name: "Bangsar", town: "KL" },
        ],
        services: ["Reformer pilates", "Group classes", "Personal training"],
        buyGuess: "visit",
        buyWhy: "a studios page with two addresses, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: READ_PAGES,
    },
    nameTokens: ["collective"],
    languageShare: { ms: 0.1, zh: 0.15 },
    words: {
        pain_point: research("back pain", "lower back pain"),
        pain_point_2: research("poor posture", "tight hips"),
        condition: site("diastasis recti", "scoliosis"),
        common_concern: research("a first class as a beginner", "an old injury"),
        core_service: services("reformer pilates", "reformer classes"),
        secondary_service_1: services("mat pilates classes", "group pilates classes"),
        secondary_service_2: site("prenatal pilates", "pregnancy pilates"),
        secondary_service_3: site("clinical pilates", "pilates for rehab"),
        secondary_service_4: services("private pilates sessions", "one-to-one pilates"),
        secondary_service_5: site("postnatal pilates"),
        secondary_service_6: site("barre classes"),
        differentiator: site("tower reformer classes", "jumpboard classes"),
        audience_1: research("beginners", "desk workers"),
        audience_2: research("office workers", "runners"),
        audience_3: research("seniors"),
        priced_service: site("a reformer class", "a private session"),
        price_point: site("RM65", "RM80"),
        promo_phrase: site("trial class promo", "intro pack"),
        place_noun: research("pilates studio", "reformer studio"),
        category: site("pilates instructor"),
        alternative_category: research("a yoga studio", "a physio"),
        situational: research("early in the morning", "after work"),
        situational_2: research("on weekends", "at lunchtime"),
    },
    places: {
        ss15: { building: "SS15 Courtyard", transit: "SS15 LRT", landmark: "Subang Parade" },
        bangsar: { building: "Bangsar Village II", transit: "Bangsar LRT", landmark: "Telawi Street" },
    },
    searches: [
        { query: "pilates ss15", searches: 1900, clicks: 120, position: 4.2, match: { place_noun: "pilates studio", area: "SS15" } },
        { query: "reformer pilates bangsar", searches: 720, clicks: 44, position: 6.8, match: { core_service: "reformer pilates", area: "Bangsar" } },
    ],
    rewrites: {},
    existing: {
        businessId: "pilates-collective",
        business: PILATES_BUSINESS,
        recipe: OLD_LOCAL_RECIPE,
        assessmentId: "pilates-collective-latest",
        oldTemplates: {
            "L1@SS15": "Best {category} in {area}?",
            "L3@SS15": "Best {category} in {area} for back pain?",
            "L7@SS15": "{category} in {area} for posture correction",
            "L1@Bangsar": "Best {category} in {area}?",
            "L7@Bangsar": "{category} in {area} for posture correction",
            C27: "Which {category} in {city} is best for back pain?",
            C29: "Which {category} in {city} helps with posture?",
            C30: "Which {category} in {city} do people recommend most?",
            C47: "Which {category} in {city} has a free trial session?",
        },
        lastResults: {
            "L1@SS15": [
                { engine: "chatgpt", named: true, rank: 2 },
                { engine: "gemini", named: true, rank: 4 },
                { engine: "google_ai", named: false, rank: null },
            ],
            "L3@SS15": [
                { engine: "chatgpt", named: false, rank: null },
                { engine: "gemini", named: false, rank: null },
                { engine: "google_ai", named: false, rank: null },
            ],
            "L7@SS15": [
                { engine: "chatgpt", named: true, rank: null },
                { engine: "gemini", named: false, rank: null },
                { engine: "google_ai", named: false, rank: null },
            ],
            "L1@Bangsar": [
                { engine: "chatgpt", named: true, rank: 1 },
                { engine: "gemini", named: true, rank: 3 },
                { engine: "google_ai", named: true, rank: 2 },
            ],
            "L7@Bangsar": [
                { engine: "chatgpt", named: false, rank: null },
                { engine: "gemini", named: false, rank: null },
                { engine: "google_ai", named: false, rank: null },
            ],
            C27: [
                { engine: "chatgpt", named: false, rank: null },
                { engine: "gemini", named: true, rank: 5 },
                { engine: "google_ai", named: false, rank: null },
            ],
            C29: [
                { engine: "chatgpt", named: false, rank: null },
                { engine: "gemini", named: false, rank: null },
                { engine: "google_ai", named: false, rank: null },
            ],
            C30: [
                { engine: "chatgpt", named: true, rank: 3 },
                { engine: "gemini", named: false, rank: null },
                { engine: "google_ai", named: false, rank: null },
            ],
        },
    },
};

/* ------------------------------------------------------------------------- *
 * riversidedental.co (existing client, one outlet)
 * ------------------------------------------------------------------------- */

const RIVERSIDE_BUSINESS: ScanDraftBusiness = {
    businessName: "Riverside Dental",
    category: "Dental clinic",
    buy: "visit",
    market: null,
    city: "PJ",
    outlets: [{ name: "Damansara Utama", town: "PJ", source: "site" }],
    services: [
        { name: "Scaling and polishing", source: "site" },
        { name: "Teeth whitening", source: "site" },
        { name: "Invisalign", source: "site" },
    ],
    languages: { ms: "auto", zh: "auto" },
    rivals: [],
    otherNames: [],
    // A sibling brand: its mentions are not this clinic's.
    notUs: ["Riverside Dental Lab"],
    requestedBy: null,
};

const RIVERSIDE: FixtureSample = {
    domain: "riversidedental.co",
    detected: {
        businessName: "Riverside Dental",
        category: "Dental clinic",
        city: "PJ",
        outlets: [{ name: "Damansara Utama", town: "PJ" }],
        services: ["Scaling and polishing", "Teeth whitening", "Invisalign"],
        buyGuess: "visit",
        buyWhy: "a contact page with one address, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: READ_PAGES,
    },
    nameTokens: ["riverside"],
    languageShare: { ms: 0.15, zh: 0.25 },
    words: {
        pain_point: research("toothache", "sensitive teeth"),
        pain_point_2: research("bleeding gums", "a chipped tooth"),
        condition: site("gum disease", "crooked teeth"),
        common_concern: research("a first check-up in years", "a fear of the dentist"),
        core_service: services("teeth cleaning", "scaling and polishing"),
        secondary_service_1: services("teeth whitening", "laser whitening"),
        secondary_service_2: services("Invisalign", "clear aligners"),
        secondary_service_3: site("dental implants", "implant crowns"),
        secondary_service_4: site("root canal treatment"),
        secondary_service_5: site("wisdom tooth removal"),
        secondary_service_6: site("kids dental check-ups", "fluoride treatment"),
        differentiator: site("same-day crowns", "3D scans"),
        audience_1: research("children", "teenagers"),
        audience_2: research("nervous patients", "seniors"),
        audience_3: research("seniors", "expats"),
        priced_service: site("scaling and polishing", "a dental check-up"),
        price_point: site("RM150", "RM120"),
        promo_phrase: site("new patient promo", "whitening promo"),
        place_noun: research("dental clinic", "dentist"),
        category: site("dentist"),
        alternative_category: research("a government dental clinic", "a dental chain"),
        situational: research("on Sunday", "after work"),
        situational_2: research("after 7pm", "on public holidays"),
    },
    places: {
        "damansara utama": { building: "Damansara Uptown", transit: "Taman Bahagia LRT", landmark: "SS2 Night Market" },
    },
    searches: [
        { query: "dentist damansara utama", searches: 1300, clicks: 88, position: 5.1, match: { place_noun: "dental clinic", area: "Damansara Utama" } },
    ],
    rewrites: {},
    existing: {
        businessId: "new-clinic",
        business: RIVERSIDE_BUSINESS,
        recipe: RECIPES.visit,
        assessmentId: "new-clinic-latest",
        oldTemplates: {},
    },
};


/* ------------------------------------------------------------------------- *
 * Joined journey (journeySamples.ts)
 * ------------------------------------------------------------------------- */

const DENTAL_WORDS: Record<string, FixtureWord> = {
    pain_point: research("toothache", "sensitive teeth"),
    pain_point_2: research("bleeding gums", "a chipped tooth"),
    condition: site("crooked teeth", "gum disease"),
    common_concern: research("a first check-up in years", "a fear of the dentist"),
    core_service: services("scaling and polishing", "teeth cleaning"),
    secondary_service_1: services("teeth whitening", "laser whitening"),
    secondary_service_2: services("braces", "metal braces"),
    secondary_service_3: site("dental implants", "implant crowns"),
    secondary_service_4: site("root canal treatment"),
    secondary_service_5: site("wisdom tooth removal"),
    secondary_service_6: site("tooth-coloured fillings", "dental crowns"),
    differentiator: site("sedation dentistry", "3D scans"),
    audience_1: research("children", "teenagers"),
    audience_2: research("nervous patients", "seniors"),
    audience_3: research("seniors", "expats"),
    priced_service: site("scaling and polishing", "a dental check-up"),
    price_point: site("RM120", "RM150"),
    promo_phrase: site("new patient promo", "whitening promo"),
    place_noun: research("dental clinic", "dentist"),
    category: site("dentist"),
    alternative_category: research("a government dental clinic", "a dental chain"),
    situational: research("on Sunday", "after work"),
    situational_2: research("after 7pm", "on public holidays"),
};

const KL_PLACES: Record<string, PlaceFacts> = {
    bangsar: { building: "Bangsar Village II", transit: "Bangsar LRT", landmark: "Telawi Street" },
    "mont kiara": { building: "Publika", transit: "Mont Kiara bus hub", landmark: "Arcoris" },
    "sri hartamas": { building: "Plaza Damas", transit: "Sri Hartamas bus stop", landmark: "Hartamas Shopping Centre" },
    "section 17": { building: "Atria Shopping Gallery", transit: "Taman Jaya LRT", landmark: "Section 17 night market" },
};

const SMILE_PITCH: ScanDraftBusiness = {
    businessName: "Sample Smile Dental",
    category: "Dental clinic",
    buy: "visit",
    market: null,
    city: "KL",
    outlets: [
        { name: "Bangsar", town: "KL", source: "site" },
        { name: "Mont Kiara", town: "KL", source: "site" },
    ],
    services: [
        { name: "Scaling and polishing", source: "site" },
        { name: "Teeth whitening", source: "site" },
        { name: "Braces", source: "site" },
    ],
    languages: { ms: "auto", zh: "auto" },
    rivals: [],
    otherNames: ["Smile Dental Bangsar"],
    notUs: [],
    requestedBy: "Dana",
};

const SMILE: FixtureSample = {
    domain: "samplesmile.my",
    detected: {
        businessName: "Sample Smile Dental",
        category: "Dental clinic",
        city: "KL",
        outlets: [
            { name: "Bangsar", town: "KL" },
            { name: "Mont Kiara", town: "KL" },
        ],
        services: ["Scaling and polishing", "Teeth whitening", "Braces"],
        buyGuess: "visit",
        buyWhy: "a clinics page with two addresses, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: ["Home page", "Treatments page", "Clinics page", "Contact page"],
    },
    nameTokens: ["sample", "smile"],
    languageShare: { ms: 0.15, zh: 0.2 },
    words: DENTAL_WORDS,
    places: KL_PLACES,
    searches: [{ query: "dentist bangsar", searches: 1600, clicks: 90, position: 5.6, match: { place_noun: "dental clinic", area: "Bangsar" } }],
    rewrites: {},
    pitch: { business: SMILE_PITCH, stamp: RECIPES.visit.stamp },
};

const KIDS: FixtureSample = {
    domain: "samplekids.my",
    detected: {
        businessName: "Sample Kids Dental",
        category: "Dental clinic",
        city: "KL",
        outlets: [{ name: "Sri Hartamas", town: "KL" }],
        services: ["Kids dental check-ups", "Fluoride treatment", "Braces"],
        buyGuess: "visit",
        buyWhy: "a contact page with one address, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: READ_PAGES,
    },
    // "kids" is a category word here ("Kids dental check-ups"), not the brand: as a name
    // token it flagged every service question (33 cards). The distinctive part is "sample".
    nameTokens: ["sample"],
    languageShare: { ms: 0.1, zh: 0.1 },
    words: {
        ...DENTAL_WORDS,
        core_service: services("kids dental check-ups", "children's dental check-ups"),
        secondary_service_1: services("fluoride treatment", "fissure sealants"),
        audience_1: research("toddlers", "young children"),
        audience_2: research("nervous children", "teenagers"),
        audience_3: research("parents", "new parents"),
        category: site("paediatric dentist"),
        place_noun: research("kids dental clinic", "children's dentist"),
    },
    places: KL_PLACES,
    searches: [],
    rewrites: {},
    pitch: {
        business: {
            ...SMILE_PITCH,
            businessName: "Sample Kids Dental",
            outlets: [{ name: "Sri Hartamas", town: "KL", source: "site" }],
            services: [
                { name: "Kids dental check-ups", source: "site" },
                { name: "Fluoride treatment", source: "site" },
                { name: "Braces", source: "site" },
            ],
            otherNames: [],
            requestedBy: "YW",
        },
        stamp: RECIPES.visit.stamp,
    },
};

const LUSH: FixtureSample = {
    domain: "samplelush.my",
    // The read as staging made it: the clinic's own name and the city came back as outlets,
    // and four other names the site uses were left as "not in your outlets".
    detected: {
        businessName: "The Lush Clinic",
        category: "Aesthetic clinic",
        city: "KL",
        outlets: [
            { name: "Mont Kiara", town: "KL" },
            { name: "Kuala Lumpur", town: "Klang Valley" },
            { name: "Bukit Bintang", town: "KL" },
            { name: "Lush", town: "KL" },
        ],
        services: ["Acne scar treatment", "Pigmentation treatment", "Laser hair removal", "Facials"],
        buyGuess: "visit",
        buyWhy: "two clinic addresses, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: READ_PAGES,
    },
    siteNames: ["Contact Your Preferred", "Lush Clinic", "Shopping", "Japan"],
    nameTokens: ["lush"],
    languageShare: { ms: 0.1, zh: 0.2 },
    words: {
        pain_point: research("acne scars", "acne", "large pores"),
        pain_point_2: research("pigmentation", "dark spots"),
        condition: site("atrophic scars", "melasma"),
        common_concern: research("a first acne scar treatment", "sensitive skin"),
        core_service: services("acne scar treatment", "laser acne scar treatment"),
        secondary_service_1: services("pigmentation treatment", "pico laser"),
        secondary_service_2: services("laser hair removal", "underarm laser hair removal"),
        secondary_service_3: services("facials", "hydrafacials"),
        secondary_service_4: site("eye bag treatment", "under-eye fillers"),
        secondary_service_5: site("jaw slimming", "jaw botox"),
        secondary_service_6: site("hair loss treatment", "scalp treatment"),
        differentiator: site("doctor consultation before every treatment", "a doctor at every session"),
        audience_1: research("women", "brides-to-be"),
        audience_2: research("men", "teenagers"),
        audience_3: research("office workers", "expats"),
        priced_service: site("a facial", "an acne facial"),
        price_point: site("RM 299", "RM 250"),
        promo_phrase: site("first-time offer", "first visit promo"),
        place_noun: research("aesthetic clinic", "medical aesthetic clinic", "skin clinic"),
        category: research("a dermatologist"),
        alternative_category: research("a beauty salon", "a facial spa"),
        situational: research("on Sunday", "after work"),
        situational_2: research("after 8pm", "on public holidays"),
    },
    places: {
        "mont kiara": { building: "Arcoris", transit: "Mont Kiara bus hub", landmark: "Publika" },
        "bukit bintang": { building: "Lot 10 Shopping Centre", transit: "Bukit Bintang MRT", landmark: "Pavilion KL" },
    },
    searches: [],
    rewrites: {},
};

const SPINE: FixtureSample = {
    domain: "samplespine.my",
    detected: {
        businessName: "Sample Spine Chiropractic",
        category: "Chiropractic clinic",
        city: "Subang Jaya",
        outlets: [{ name: "SS15", town: "Subang Jaya" }],
        services: ["Chiropractic adjustment", "Spinal decompression", "Posture assessment"],
        buyGuess: "visit",
        buyWhy: "a contact page with one address, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: READ_PAGES,
    },
    nameTokens: ["sample", "spine"],
    languageShare: { ms: 0.1, zh: 0.15 },
    words: {
        pain_point: research("back pain", "lower back pain"),
        pain_point_2: research("neck pain", "stiff neck"),
        condition: site("slipped disc", "sciatica"),
        common_concern: research("a first adjustment", "cracking sounds"),
        core_service: services("chiropractic adjustment", "spinal adjustment"),
        secondary_service_1: services("spinal decompression", "disc decompression"),
        secondary_service_2: services("posture assessment", "posture correction"),
        secondary_service_3: site("sports chiropractic", "sports injury care"),
        secondary_service_4: site("pregnancy chiropractic", "prenatal chiropractic"),
        secondary_service_5: site("kids chiropractic"),
        secondary_service_6: site("headache treatment", "migraine care"),
        differentiator: site("X-rays on site", "digital posture scans"),
        audience_1: research("office workers", "desk workers"),
        audience_2: research("runners", "golfers"),
        audience_3: research("seniors", "pregnant women"),
        priced_service: site("a first consultation", "an adjustment"),
        price_point: site("RM150", "RM120"),
        promo_phrase: site("first visit promo", "new patient offer"),
        place_noun: research("chiropractic clinic", "chiropractor"),
        category: site("chiropractor"),
        alternative_category: research("a physio", "an orthopaedic doctor"),
        situational: research("on Sunday", "after work"),
        situational_2: research("after 8pm", "on public holidays"),
    },
    places: { ss15: { building: "SS15 Courtyard", transit: "SS15 LRT", landmark: "Subang Parade" } },
    searches: [],
    rewrites: {},
};

const SIGHT: FixtureSample = {
    domain: "samplesight.my",
    detected: {
        businessName: "Sample Sight Optometry",
        category: "Optometrist",
        city: "KL",
        outlets: SIGHT_OUTLETS,
        services: ["Eye test", "Progressive lenses", "Contact lenses", "Myopia control for kids"],
        buyGuess: "visit",
        buyWhy: "a stores page with 24 addresses, and no cart",
        market: null,
        confidence: "high",
        reason: null,
        pages: ["Home page", "Stores page", "Services page", "Contact page"],
    },
    nameTokens: ["sample", "sight"],
    languageShare: { ms: 0.1, zh: 0.15 },
    words: {
        pain_point: research("blurry vision", "eye strain"),
        pain_point_2: research("dry eyes", "tired eyes"),
        condition: site("myopia", "astigmatism"),
        common_concern: research("a first eye test in years", "glasses that give headaches"),
        core_service: services("eye test", "eye examination"),
        secondary_service_1: services("progressive lenses", "multifocal lenses"),
        secondary_service_2: services("contact lenses", "daily contact lenses"),
        secondary_service_3: services("myopia control for kids", "ortho-k lenses"),
        secondary_service_4: site("blue light glasses", "computer glasses"),
        secondary_service_5: site("dry eye treatment"),
        secondary_service_6: site("prescription sunglasses"),
        differentiator: site("same-day glasses", "a free eye test"),
        audience_1: research("children", "teenagers"),
        audience_2: research("seniors", "drivers"),
        audience_3: research("office workers", "students"),
        priced_service: site("progressive lenses", "an eye test"),
        price_point: site("RM590", "RM450"),
        promo_phrase: site("frame and lens promo", "second pair promo"),
        place_noun: research("optical shop", "optometrist"),
        category: site("optometrist"),
        alternative_category: research("an eye clinic", "an ophthalmologist"),
        situational: research("on Sunday", "after work"),
        situational_2: research("after 9pm", "on public holidays"),
    },
    places: {},
    searches: [],
    rewrites: {},
};

/* ------------------------------------------------------------------------- *
 * Lookup
 * ------------------------------------------------------------------------- */

const SAMPLES: FixtureSample[] = [PHYSIO, SLEEP, SUPPS, GLOW, PILATES, RIVERSIDE, SMILE, KIDS, LUSH, SPINE, SIGHT];

/**
 * The wider town customers type for an area that is also staff's town, for
 * the "<Area> is both the outlet and its town" decision. Null: none (Subang
 * Jaya is the town people type). An area not listed has none either.
 */
export const WIDER_TOWN: Record<string, string | null> = {
    bangsar: "KL",
    "mont kiara": "KL",
    "damansara heights": "KL",
    cheras: "KL",
    "subang jaya": null,
    // PJ is Petaling Jaya itself, not a wider town.
    "petaling jaya": null,
};

export function widerTown(area: string): string | null {
    return WIDER_TOWN[area.trim().toLowerCase()] ?? null;
}

/** A domain as the samples key it: lower case, no scheme, no www, no trailing slash. */
export function domainKey(domain: string): string {
    return domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .replace(/\/+$/, "");
}

export function sampleFor(domain: string): FixtureSample | null {
    const key = domainKey(domain);
    return SAMPLES.find((s) => s.domain === key) ?? null;
}

export function sampleForBusiness(businessId: string): FixtureSample | null {
    return SAMPLES.find((s) => s.existing?.businessId === businessId) ?? null;
}

/** What the website read returns for a domain with no sample: low confidence, every field empty. */
export function unknownRead(): ScanDraftDetected {
    return {
        businessName: "",
        category: "",
        city: "",
        outlets: [],
        services: [],
        buyGuess: null,
        buyWhy: null,
        market: null,
        confidence: "low",
        reason: null,
        pages: READ_PAGES,
    };
}

/** The words a domain with no sample starts from: only what the Business step gave. */
export function fallbackWords(business: ScanDraftBusiness): Record<string, FixtureWord> {
    const category = business.category.trim().toLowerCase();
    const names = business.services.map((s) => s.name.trim().toLowerCase()).filter(Boolean);
    const out: Record<string, FixtureWord> = {};
    const given = (value: string, origin: FixtureWord["origin"]): FixtureWord =>
        value ? { value, origin, alternatives: [] } : { value: "", origin: "fallback", alternatives: [], missing: true };
    out.place_noun = given(category, "site");
    out.product_noun = given(category, "site");
    out.store_noun = given(category ? `${category} shop` : "", "site");
    out.category = given(category, "site");
    out.core_service = given(names[0] ?? "", "services");
    out.core_product = given(names[0] ?? "", "services");
    for (let i = 1; i <= 6; i++) out[`secondary_service_${i}`] = given(names[i] ?? "", "services");
    return out;
}
